import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Calculator, TrendingUp, ArrowRight, Info, Loader2, MessageCircle, Calendar, CheckCircle, Target, Heart, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE}?text=Hi,%20I%20used%20the%20Monthly%20Savings%20Calculator%20on%20FutureSanchay%20and%20want%20to%20discuss%20plans`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 1500, prefix = "", suffix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.5 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const MonthlySavingCalculator = () => {
  const [monthlyAmount, setMonthlyAmount] = useState(10000);
  const [age, setAge] = useState(30);
  const [duration, setDuration] = useState(20);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateSavings = async () => {
    if (monthlyAmount < 5000) {
      toast.error("Minimum monthly amount is ₹5,000");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/calculator/monthly-saving`, {
        monthly_amount: monthlyAmount,
        age: age,
        duration: duration,
      });
      setResult(response.data);
      toast.success("Calculation complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Calculation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white page-transition" data-testid="monthly-saving-calculator-page">
      <Helmet>
        <title>LIC Savings Calculator | See How ₹5,000/Month Grows | FutureSanchay</title>
        <meta name="description" content="Free LIC savings calculator. See how your monthly savings of ₹5,000+ can grow over 10-40 years. Get illustrative maturity values. No signup required." />
        <link rel="canonical" href="https://futuresanchay.com/calculator/monthly-saving" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-testid="calculator-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-emerald-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Monthly Saving Calculator</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <Calculator className="w-4 h-4" />
              Free Planning Tool
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 animate-fade-in-up delay-100">
              Monthly Saving Calculator
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              See how your <span className="font-semibold text-sky-500">disciplined monthly savings</span> can grow over time. Start with just ₹5,000/month.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="pb-12 md:pb-16">
        <div className="container-main">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Input Form */}
            <div className="lg:col-span-7 animate-fade-in-up delay-300">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-6 md:p-8 hover:shadow-2xl transition-all duration-500" data-testid="calculator-form">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-sky-500" />
                  </div>
                  Enter Your Details
                </h2>

                <div className="space-y-8">
                  {/* Monthly Amount */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Monthly Saving Amount</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{formatCurrency(monthlyAmount)}</span>
                    </div>
                    <Slider
                      value={[monthlyAmount]}
                      onValueChange={(value) => setMonthlyAmount(value[0])}
                      min={5000}
                      max={100000}
                      step={1000}
                      className="mb-2"
                      data-testid="monthly-amount-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>₹5,000</span>
                      <span>₹1,00,000</span>
                    </div>
                  </div>

                  {/* Age */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Your Current Age</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{age} years</span>
                    </div>
                    <Slider
                      value={[age]}
                      onValueChange={(value) => setAge(value[0])}
                      min={18}
                      max={55}
                      step={1}
                      className="mb-2"
                      data-testid="age-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>18 years</span>
                      <span>55 years</span>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Investment Duration</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{duration} years</span>
                    </div>
                    <Slider
                      value={[duration]}
                      onValueChange={(value) => setDuration(value[0])}
                      min={10}
                      max={40}
                      step={1}
                      className="mb-2"
                      data-testid="duration-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>10 years</span>
                      <span>40 years</span>
                    </div>
                  </div>

                  <Button
                    onClick={calculateSavings}
                    disabled={isLoading}
                    className="w-full h-14 bg-sky-500 hover:bg-sky-600 text-white rounded-2xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 animate-pulse-glow"
                    data-testid="calculate-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Calculating...</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2" /> Calculate Maturity</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-5 animate-fade-in-up delay-400">
              {result ? (
                <div className="space-y-4 animate-scale-in" data-testid="calculator-result">
                  {/* Maturity Card */}
                  <div className="bg-gradient-to-br from-sky-500 to-sky-600 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-sky-500 via-emerald-500 to-sky-500 opacity-20 animate-gradient"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-sky-200" />
                        <p className="text-sky-100 text-sm">Illustrative Maturity Range</p>
                      </div>
                      <p className="text-4xl font-bold mb-1">{formatCurrency(result.result.maturity_range_low)}</p>
                      <p className="text-sky-200 text-sm">to {formatCurrency(result.result.maturity_range_high)}</p>
                      
                      <div className="border-t border-sky-400/30 mt-4 pt-4 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sky-200 text-xs">Total Premium</p>
                          <p className="font-semibold text-lg">{formatCurrency(result.result.total_premium_paid)}</p>
                        </div>
                        <div>
                          <p className="text-sky-200 text-xs">Potential Gain</p>
                          <p className="font-semibold text-lg text-emerald-300">+{formatCurrency(result.result.potential_gain_low)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg hover:shadow-xl transition-all">
                    <h4 className="font-semibold text-gray-900 mb-2">Want personalized advice?</h4>
                    <p className="text-gray-500 text-sm mb-4">Our IRDAI certified experts can help you choose the right plan.</p>
                    <div className="space-y-2">
                      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block">
                        <Button className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all hover:scale-105 animate-whatsapp-pulse" data-testid="result-whatsapp-btn">
                          <MessageCircle className="w-5 h-5 mr-2" /> Chat on WhatsApp
                        </Button>
                      </a>
                      <Link to="/contact" className="block">
                        <Button variant="outline" className="w-full h-12 rounded-xl font-semibold border-2 border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all" data-testid="result-callback-btn">
                          <Calendar className="w-5 h-5 mr-2" /> Book a Call
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs text-amber-800 flex gap-2">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {result.disclaimer}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-100 p-8 text-center shadow-lg" data-testid="calculator-placeholder">
                  <div className="w-20 h-20 bg-sky-100 rounded-3xl flex items-center justify-center mx-auto mb-4 animate-float">
                    <TrendingUp className="w-10 h-10 text-sky-500" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-xl mb-2">Your Results Will Appear Here</h3>
                  <p className="text-gray-500">Adjust the sliders and click calculate to see your potential savings growth.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Other Calculators */}
      <section className="pb-16 bg-gray-50">
        <div className="container-main py-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Try Other Calculators</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/calculator/retirement" className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 shine-effect" data-testid="other-calc-retirement">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-emerald-500" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">Retirement Gap Calculator</h4>
              <p className="text-gray-500 text-sm mb-3">Find out if you're on track for retirement.</p>
              <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Try now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/calculator/child-education" className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 shine-effect" data-testid="other-calc-education">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Heart className="w-6 h-6 text-amber-500" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">Child Education Calculator</h4>
              <p className="text-gray-500 text-sm mb-3">Plan for your child's higher education.</p>
              <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Try now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MonthlySavingCalculator;
